import {Formik} from 'formik';

import {FormComponent} from './FormComponent';

import {RecordSchema} from './RecordSchema';
import {toFormikValidationSchema} from 'zod-formik-adapter';

import {FormWithButtons} from '@flexinet/ui-components';

import {
    BillingPeriod,
    Price,
    PriceStatus,
    PricingModel,
    RecurringOption
} from '../types';
import {useParams} from "react-router-dom";
import {stage} from "../../../config/stage";

export const RecordForm = ({
                               record,
                               handleSubmit,
                               handleCancel,
                           }: {
    record: Omit<Price, 'id'>,
    handleSubmit: (values: Price) => void,
    handleCancel?: () => void
}) => {

    // The `:id` route param means DIFFERENT things depending on where this form is
    // rendered from:
    //   /products/:id/prices  -> :id is the PRODUCT id   (creating a price under a product)
    //   /prices/:id           -> :id is the PRICE id     (editing an existing price)
    // This used to be read unconditionally as `productId` and stamped onto every save, so
    // editing from the price detail page wrote the PRICE's own id into productId — the
    // record's product link was destroyed on every save, Price.product then resolved to
    // null (surfacing as "Cannot return null for non-nullable type: 'String' within parent
    // 'Product'"), and the row became invisible to listPrices, which queries BY productId.
    //
    // An existing record already knows its product. Only fall back to the route param when
    // there is nothing on the record — i.e. the create-under-a-product case.
    const {id: routeId} = useParams()

    const onSubmit = (values: Price) => {

        values.amount = values.amount * 100;

        // Prefer the record's OWN product when editing; fall back to the route param only
        // when the record has none — the create-under-a-product case, where :id IS the
        // product id. This is what stops an edit from the price page overwriting the link.
        //
        // NOTE: this stops FURTHER corruption; it does not repair rows already carrying
        // productId == their own id. Those need a data fix (re-create the price under the
        // right product, or a one-shot repair script).
        const resolvedProductId = record?.productId ?? routeId;

        if (!resolvedProductId) {
            throw new Error('productId is required');
        }
        values.productId = resolvedProductId;

        handleSubmit(values);
    };

    const onCancel = () => {
        handleCancel?.();
    };


    let initialValues = {
        recurringOption: RecurringOption.Yes,
        pricingModel: PricingModel.FlatRate,
        billingPeriod: BillingPeriod.Monthly,
        priceStatus: PriceStatus.Active
    } as Price;

    if (record) {
        const pennies = record.amount / 100;
        initialValues = Object.assign({}, initialValues, record, {amount: pennies});
    }

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={toFormikValidationSchema(RecordSchema)}
            onSubmit={onSubmit}
        >
            {({
                  handleSubmit,
                  isSubmitting,
                  ...rest
              }) => (
                <FormWithButtons handleSubmit={handleSubmit} isSubmitting={isSubmitting}
                                 handleCancel={onCancel} debug={false && stage === 'ide'} {...rest}  >
                    <FormComponent {...rest} />
                </FormWithButtons>)}
        </Formik>
    );
};
